import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { abmeldenUrl, type Ich } from '../api';
import { Avatar } from './Avatar';
import { Icon } from './Icon';
import { effectiveTheme, toggleTheme } from '../theme';
import { APP_VERSION } from '../version';

const Einstellungen = lazy(() => import('./Benachrichtigungen').then((m) => ({ default: m.EinstellungenDialog })));
const KalenderAbo = lazy(() => import('./KalenderAbo'));
const Versionen = lazy(() => import('./Versionen'));

/** Name ohne Adresse, sonst die Adresse (solange Graph keinen Anzeigenamen liefert). */
export const anzeigename = (ich: Pick<Ich, 'email' | 'name'>) => (ich.name && ich.name !== ich.email ? ich.name : ich.email);

/**
 * Bild und Name in der Kopfleiste; Klick öffnet das Menü mit den eigenen Einstellungen, dem Design und „Abmelden“.
 * mitName=false zeigt nur das Bild (Projektansicht, dort ist die Kopfleiste voll).
 */
export function Benutzermenue({ ich, mitName = true }: { ich: Ich; mitName?: boolean }) {
  const [offen, setOffen] = useState(false);
  const [einstellungen, setEinstellungen] = useState(false);
  const [kalender, setKalender] = useState(false);
  const [versionen, setVersionen] = useState(false);
  const [theme, setTheme] = useState(effectiveTheme());
  const dark = theme === 'dark';
  const ref = useRef<HTMLDivElement>(null);
  const name = anzeigename(ich);

  // Aufklappmenü wie „Export“: Klick daneben und Esc schließen
  useEffect(() => {
    if (!offen) return;
    const zu = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('pointerdown', zu);
    window.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', zu); window.removeEventListener('keydown', esc); };
  }, [offen]);

  return (
    <div className="menu benutzermenue" ref={ref}>
      <button className={'btn person-btn' + (offen ? ' on' : '')} onClick={() => setOffen(!offen)} aria-expanded={offen}
        title={`${name} – Konto und Einstellungen`} data-testid="benutzermenue">
        <Avatar email={ich.email} name={ich.name} size={26} />
        {mitName && <span className="lbl" data-testid="ich">{name}{ich.superadmin ? <span className="hint"> · Superadmin</span> : null}</span>}
      </button>
      {offen && (
        <div className="pop benutzermenue-pop" data-testid="benutzermenue-pop">
          <div className="konto">
            <Avatar email={ich.email} name={ich.name} size={40} />
            <div><strong>{name}</strong><div className="hint">{ich.email}</div>{ich.superadmin && <div className="hint">Superadmin</div>}</div>
          </div>
          <hr />
          <button onClick={() => { setOffen(false); setEinstellungen(true); }} data-testid="benutzer-benachrichtigungen"><Icon name="bell" /> Benachrichtigungen einstellen …</button>
          <button onClick={() => { setOffen(false); setKalender(true); }} data-testid="benutzer-kalender"><Icon name="kalender" /> Kalender abonnieren …</button>
          <button onClick={() => toggleTheme(setTheme)} data-testid="toggle-theme"><Icon name={dark ? 'sun' : 'moon'} /> {dark ? 'Helles Design' : 'Dunkles Design'}</button>
          <hr />
          <a className="pop-link" href={abmeldenUrl()} data-testid="benutzer-abmelden"><Icon name="x" /> Abmelden</a>
          <button className="hint app-version" title={'Versionshinweise\nWas sich mit welcher Version geändert hat'}
            onClick={() => { setOffen(false); setVersionen(true); }} data-testid="app-version">Version {APP_VERSION}</button>
        </div>
      )}
      {versionen && createPortal(
        <Suspense fallback={null}>
          <Versionen onClose={() => setVersionen(false)} />
        </Suspense>,
        document.body,
      )}
      {kalender && createPortal(
        <Suspense fallback={null}>
          <KalenderAbo onClose={() => setKalender(false)} />
        </Suspense>,
        document.body,
      )}
      {einstellungen && createPortal(
        <Suspense fallback={null}>
          <Einstellungen onClose={() => setEinstellungen(false)} />
        </Suspense>,
        document.body,
      )}
    </div>
  );
}
